import { Alert, Button, Flex, Form, message, Modal } from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { usePersonsServiceGetPersonListByDataEndpoint } from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { Text } from 'src/components'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { PersonalDataSearch } from './PersonalDataSearch'
import { useStyles } from './styles'

type Props = {
  form?: FormInstance
  onCancel?: () => void
  formConsultation?: FormInstance
}
export const AddPersonAlert = ({ form, onCancel, formConsultation }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const params = Form.useWatch([], form)
  const shouldSearchInMConnect = Form.useWatch('shouldSearchInMConnect', form)

  const filteredParams = Object.fromEntries(
    Object.entries(params ?? {}).filter(([_, value]) => value !== '')
  )

  const onToggle = () => setIsModalOpen((prev) => !prev)

  const birthDate =
    filteredParams.birthDate && dayjs.isDayjs(filteredParams?.birthDate)
      ? filteredParams.birthDate.format(yearMonthDaySlash)
      : undefined

  const { data, isFetching, error, refetch } =
    usePersonsServiceGetPersonListByDataEndpoint(
      {
        shouldSearchInMConnect: true,
        ...filteredParams,
        birthDate
      },
      [],
      { enabled: false }
    )

  const onClick = async () => {
    if (!shouldSearchInMConnect) {
      form?.resetFields([
        'citizenship',
        'lastName',
        'firstName',
        'birthDate',
        'gender'
      ])
      form?.setFieldsValue({
        shouldSearchInMConnect: true,
        citizenship: 'MD'
      })
    } else {
      await form?.validateFields(['idnp'])
      setHasSearched(true)
      refetch()
    }
  }

  useEffect(() => {
    const typedError = error as {
      body: {
        errors: ApiError
      }
    }

    if (typedError) formErrorHandler(typedError?.body?.errors)
  }, [error])

  useEffect(() => {
    if (
      hasSearched &&
      !error &&
      shouldSearchInMConnect &&
      !isFetching &&
      !data?.data?.length
    ) {
      message.error(t('persons.no_person_found'))
    }
  }, [data, shouldSearchInMConnect, hasSearched, error, isFetching])

  return (
    <>
      <Form.Item name="shouldSearchInMConnect" hidden />
      <Alert
        type="warning"
        className={styles.alert}
        showIcon
        message={
          <Flex align="center" justify="space-between">
            <Text weight={700}>{t('analysis.add_analysis.no_code_found')}</Text>

            <Flex gap={8}>
              {shouldSearchInMConnect && data?.data?.length === 0 && (
                <Button onClick={onToggle}>
                  {t('persons.add_new_person')}
                </Button>
              )}
              <Button type="primary" onClick={onClick}>
                {t(
                  shouldSearchInMConnect
                    ? 'transfer.search_in_mconnect'
                    : 'transfer.take_data_from_mconnnect'
                )}
              </Button>
            </Flex>
          </Flex>
        }
      />
      {isModalOpen && (
        <Modal
          open
          width={600}
          maskClosable={false}
          title={t('consultations.contacts_add')}
          centered
          footer={false}
          onCancel={onToggle}
        >
          <PersonalDataSearch
            isAddPersonAlertOpen
            onCancel={onCancel}
            onCloseModal={onToggle}
            form={formConsultation}
          />
        </Modal>
      )}
    </>
  )
}
